import { Component } from '@theme/component';

/** A source-linked review rail. No external requests or duplicated review cards. */
class SolReviews extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.initialize();
  }

  initialize() {
    this.cleanup();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.track = this.querySelector('[data-reviews-track]');
    this.cards = Array.from(this.track?.children || []);
    this.pauseButton = this.querySelector('[data-reviews-pause]');
    this.status = this.querySelector('[data-reviews-status]');
    this.counter = this.querySelector('[data-reviews-counter]');
    this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.userPaused = this.motion.matches || this.dataset.autoplay !== 'true';
    this.pointerPaused = false;
    this.focusPaused = this.contains(document.activeElement);
    this.inView = false;
    this.index = 0;
    const hideFailedImage = image => {
      if (image.classList.contains('sol-review-avatar')) image.hidden = true;
      else image.closest('.sol-review-photo')?.setAttribute('hidden', '');
    };
    this.addEventListener('error', event => {
      if (event.target instanceof HTMLImageElement) hideFailedImage(event.target);
    }, { capture: true, signal });
    for (const image of this.querySelectorAll('img')) {
      if (image.complete && image.naturalWidth === 0) hideFailedImage(image);
    }
    if (!this.track || this.cards.length < 2) return;
    this.querySelector('[data-reviews-controls]')?.removeAttribute('hidden');
    this.addEventListener('click', event => {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('[data-reviews-prev]')) this.move(-1, true);
      if (event.target.closest('[data-reviews-next]')) this.move(1, true);
      if (event.target.closest('[data-reviews-pause]')) {
        this.userPaused = !this.userPaused;
        this.updatePauseButton();
        this.schedule();
      }
    }, { signal });
    this.addEventListener('pointerenter', event => {
      if (event.pointerType !== 'mouse') return;
      this.pointerPaused = true;
      this.schedule();
    }, { signal });
    this.addEventListener('pointerleave', event => {
      if (event.pointerType !== 'mouse') return;
      this.pointerPaused = false;
      this.schedule();
    }, { signal });
    this.addEventListener('focusin', () => {
      this.focusPaused = true;
      this.schedule();
    }, { signal });
    this.addEventListener('focusout', event => {
      this.focusPaused = event.relatedTarget instanceof Node && this.contains(event.relatedTarget);
      this.schedule();
    }, { signal });
    this.track.addEventListener('pointerdown', () => {
      if (this.pauseButton) this.userPaused = true;
      this.updatePauseButton();
      this.schedule();
    }, { signal });
    this.track.addEventListener('keydown', event => {
      if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      this.move(event.key === 'ArrowRight' ? 1 : -1, true);
    }, { signal });
    this.track.addEventListener('scroll', () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.updatePosition();
      });
    }, { passive: true, signal });
    document.addEventListener('visibilitychange', () => this.schedule(), { signal });
    this.motion.addEventListener('change', () => {
      if (this.motion.matches) this.userPaused = true;
      this.updatePauseButton();
      this.schedule();
    }, { signal });
    this.intersection = new IntersectionObserver(entries => {
      this.inView = entries.some(entry => entry.isIntersecting);
      this.schedule();
    }, { threshold: 0.15 });
    this.intersection.observe(this);
    this.resize = new ResizeObserver(() => {
      this.updatePosition();
      this.schedule();
    });
    this.resize.observe(this.track);
    this.updatePosition();
    this.updatePauseButton();
  }

  updatePosition() {
    if (!this.track || !this.cards.length) return;
    const bounds = this.track.getBoundingClientRect();
    this.hasOverflow = this.track.scrollWidth - this.track.clientWidth > 2;
    const controls = this.querySelector('[data-reviews-controls]');
    if (controls) controls.hidden = !this.hasOverflow;
    const rtl = getComputedStyle(this.track).direction === 'rtl';
    const edge = rtl ? bounds.right : bounds.left;
    let nearest = Infinity;
    this.cards.forEach((card, index) => {
      const rect = card.getBoundingClientRect();
      const distance = Math.abs((rtl ? rect.right : rect.left) - edge);
      if (distance < nearest) {
        nearest = distance;
        this.index = index;
      }
    });
    const visible = this.cards.flatMap((card, index) => {
      const rect = card.getBoundingClientRect();
      const width = Math.min(rect.right, bounds.right) - Math.max(rect.left, bounds.left);
      return width > rect.width * 0.6 ? [index + 1] : [];
    });
    const first = visible[0] || this.index + 1;
    const last = visible[visible.length - 1] || first;
    if (this.counter) this.counter.textContent = `${first === last ? first : `${first}–${last}`} / ${this.cards.length}`;
  }

  move(direction, manual = false) {
    if (!this.track || !this.cards.length) return;
    if (manual && this.pauseButton) {
      this.userPaused = true;
      this.updatePauseButton();
    }
    this.updatePosition();
    const firstCard = this.cards[0].getBoundingClientRect();
    const secondCard = this.cards[1].getBoundingClientRect();
    const step = Math.abs(secondCard.left - firstCard.left) || firstCard.width;
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const maxIndex = Math.max(0, Math.ceil((maxScroll - 2) / step));
    const next = (Math.min(this.index, maxIndex) + direction + maxIndex + 1) % (maxIndex + 1);
    const bounds = this.track.getBoundingClientRect();
    const target = this.cards[next].getBoundingClientRect();
    const rtl = getComputedStyle(this.track).direction === 'rtl';
    const delta = rtl ? target.right - bounds.right : target.left - bounds.left - 1;
    const wrap = direction > 0 ? next < this.index : next > this.index;
    this.track.scrollBy({ left: delta, behavior: this.motion.matches || wrap ? 'instant' : 'smooth' });
    if (manual && this.status) {
      this.status.textContent = this.dataset.slideLabel.replace('[current]', String(next + 1)).replace('[total]', String(this.cards.length));
    }
    this.schedule();
  }

  updatePauseButton() {
    if (!this.pauseButton) return;
    this.pauseButton.setAttribute('aria-pressed', String(this.userPaused));
    this.pauseButton.textContent = this.userPaused ? this.dataset.playLabel : this.dataset.pauseLabel;
  }

  schedule() {
    clearTimeout(this.timer);
    this.timer = null;
    if (!this.isConnected || this.userPaused || this.pointerPaused || this.focusPaused || !this.inView || document.hidden || !this.hasOverflow || this.cards.length < 2) return;
    this.timer = window.setTimeout(() => this.move(1), 6500);
  }

  cleanup() {
    this.controller?.abort();
    clearTimeout(this.timer);
    cancelAnimationFrame(this.frame);
    this.frame = null;
    this.intersection?.disconnect();
    this.resize?.disconnect();
  }

  disconnectedCallback() {
    this.cleanup();
    super.disconnectedCallback();
  }
}

if (!customElements.get('sol-reviews')) customElements.define('sol-reviews', SolReviews);
